import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router';
import ContentHeaderImage from '../../components/ContentHeaderImage';
import RecommendationSlider from '../../components/RecommendationSlider';
import PriceInfoBox from '../../components/PriceInfoBox';
import ExcursionDuration from '../../components/ExcursionDuration';
import ExcursionAttributes from '../../components/ExcursionAttributes';
import ContentDividerWithIcon from '../../components/ContentDividerWithIcon';
import PlannerContainer from '../../components/PlannerContainer';
import Modal from '../../components/Modal';
import ModalGallery from '../../components/ModalGallery';
import AddToPlannerForm from '../../components/AddToPlannerForm';
import { getFromHub } from "../../actions/networkActions";
import { formatPortDate, formatTimeHM } from '../../utils/dateTimeUtils';
import ImportantInformationEntry from '../../components/ImportantInformationEntry';
import BookedPassengersList from '../../components/BookedPassengersList';
import { imagePath, themableImagePath } from '../../utils/imageUtils';
import ImageGallery from 'react-image-gallery';
import SoldOutBox from '../../components/SoldOutBox';
import GalleryToggleButton from "../../components/GalleryToggleButton";
import SuccessModal from "../../components/SuccessModal";
import { setScrollToItineraryEntry } from "../../actions/itineraryActions";
import { getHubBaseUrl, useExcursionTags } from "../../utils/configUtils";
import Exp360VideoPlayerSimple from "../../components/Exp360VideoPlayerSimple";
import ThemableSvgIcon from "../../components/theme/ThemableSvgIcon";
import {
    getExcursionsText,
    getExcursionText,
} from "../../utils/themeUtils";
import ExcursionTags from "../../components/ExcursionTags";
import BookingCode from "../../components/BookingCode";
import DepartureTime from "../../components/DepartureTime";
import { Trans, useLingui } from '@lingui/react/macro';
import { getTags } from '../../actions/tagsActions';
import { getTranslation } from '../../utils/translationUtils';
import { removeHtmlStyles } from '../../utils/stringUtils';

const ExcursionDetail = () => {
    const { itineraryId, excursionId, modal } = useParams();
    const navigate = useNavigate();
    const cart = useSelector(state => state.cart.excursions);
    const {t} = useLingui();

    const [images, setImages] = useState(null);
    const [exp360BundleId, setExp360BundleId] = useState("");
    const [showPassengerModal, setShowPassengerModal] = useState(modal === "passengers");
    const [showImagesModal, setShowImagesModal] = useState(false);
    const [showExp360Modal, setShowExp360Modal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [data, setData] = useState({
        excursion: null,
        duration: 0,
        itinerary: null,
        currentAvailability: 0,
        description: "",
        notes: "",
        fromTime: "",
        endTime: "",
        isBookingAllowed: false,
        waitlistActivated: false,
        currentWaitlistAvailability: 0,
        excursionLoaded: false,
        descriptionLoaded: false,
    });

    useEffect(() => {
        loadData();
        getTags();
    }, [itineraryId, excursionId]);

    const loadData = useCallback(() => {
        const excursionUrl = `prebooking/cruises/itinerary/${itineraryId}/excursions/${excursionId}`;
        getFromHub(excursionUrl, updateExcursion);
        getFromHub(`blobs/images/${excursionId}`, updateImages);
        getFromHub(`exp360/${excursionId}`, updateExp360BundleId);
    }, [itineraryId, excursionId]);

    const updateExcursion = (response) => {
        setData(prev => ({
            ...prev,
            excursion: response.excursion,
            duration: response.duration,
            itinerary: { ...response.itinerary, dayOfCruise: response.dayOfCruise },
            currentAvailability: response.currentAvailability,
            fromTime: response.fromTime,
            endTime: response.toTime,
            isBookingAllowed: response.isBookingAllowed,
            waitlistActivated: response.waitlistActivated,
            currentWaitlistAvailability: response.currentWaitlistAvailability,
            excursionLoaded: true,
        }));
        getFromHub(`descriptions/${response.excursion.id}`, updateDescription);
    };

    const updateDescription = (response) => {
        const descriptionObj = response.find((desc) => desc.descriptionType === "Description");
        const descriptionValue = (descriptionObj ? getTranslation(descriptionObj.value) : t({id: "description_missing"}));
        const notesObj = response.find((desc) => desc.descriptionType === "Notes");
        const notesValue = (notesObj ? getTranslation(notesObj.value) : "");
        setData(prev => ({
            ...prev,
            description: descriptionValue,
            notes: notesValue,
            descriptionLoaded: true,
        }));
    };

    const updateImages = (imagesResponse) => {
        const imgs = imagesResponse.map((image) => {
            return {
                thumbnail: `${getHubBaseUrl(false)}${image.url}&width=200`,
                original: `${getHubBaseUrl(false)}${image.url}`,
            }
        });
        setImages(imgs);
    };

    const updateExp360BundleId = (bundleResponse) => {
        let exp360BundleId = "";
        if (bundleResponse.length) {
            exp360BundleId = bundleResponse[0].snippet;
        }
        setExp360BundleId(exp360BundleId);
    };

    const togglePassengerModal = () => setShowPassengerModal((prev) => !prev);
    const toggleImagesModal = () => setShowImagesModal((prev) => !prev);
    const toggleExp360Modal = () => setShowExp360Modal((prev) => !prev);

    const showSuccessModalHandler = (successMessage) => {
        setScrollToItineraryEntry(itineraryId);
        setShowPassengerModal(false);
        setShowSuccessModal(true);
        setSuccessMessage(successMessage);
    };

    const closeSuccessModal = () => {
        setScrollToItineraryEntry(null);
        setShowSuccessModal(false);
    };

    if (!data.excursionLoaded || !data.descriptionLoaded) {
        return (<div><span translate="no"><Trans id="loading">loading</Trans>...</span></div>);
    }

    const isExcursionInCart = !!(cart?.[itineraryId]?.[excursionId]);
    const isWaitlist = data.currentAvailability <= 0 || data.waitlistActivated;
    const buttonText = isExcursionInCart
        ? isWaitlist ? t({id: "added_to_waitlist"}) : t({id: "added_to_planner"})
        : isWaitlist ? t({id: "add_to_waitlist"}) : t({id: "add_to_planner"});

    const excursionAttributes = useExcursionTags()
        ? data.excursion.tags.map(tag => getTranslation(tag.tagName))
        : data.excursion.categories.map(category => getTranslation(category.categoryName));

    const excursionFacts = [
        t({ 
            id: 'day_of_itinerary_text', 
            message: `Day ${data.itinerary.dayOfCruise} of your itinerary (${formatPortDate(data.itinerary.date)})` 
        }),
        t({ 
            id: 'port_times_text', 
            message: `From ${getTranslation(data.itinerary.port.name).toUpperCase()} where the ship is in port: ${formatTimeHM(data.itinerary.arrival, "Previous day")} - ${formatTimeHM(data.itinerary.departure, "Next day")}`
        })
      ];
    
    const excursionsText = getExcursionsText();
    const excursionText = getExcursionText();
    const portName = getTranslation(data.excursion.port.name).toUpperCase();

    const renderExcursionDetailPrice = () => {
        const bookingAllowed = data.isBookingAllowed;
        const hasAvailability = data.currentAvailability > 0;
        const waitlistActive = data.waitlistActivated;
        const hasWaitlistAvailability = data.currentWaitlistAvailability > 0;
        const isIncludedExcursion = data.excursion.included ? true : false;

        const canBookNow = bookingAllowed && hasAvailability && !waitlistActive;
        const shouldShowSoldOut = !bookingAllowed || !hasAvailability || waitlistActive;
        const canWaitlistBook = bookingAllowed && waitlistActive && hasWaitlistAvailability;

        if(isIncludedExcursion){
            return(
                <div className="w-full text-grey-darkest bg-grey-lighter p-2 md:p-4">
                    <div className="uppercase text-xl mb-2">
                        Included experience
                    </div>
                    <div>
                        This excursion is included as part of the package.
                    </div>
                </div>
            )
        }

        return (
            <>
                {canBookNow && (
                    <PriceInfoBox
                        classes="w-full text-primary-dark border border-primary-light p-4"
                        adultPrice={data.excursion.price.adult}
                        childPrice={data.excursion.price.child}
                        currencySymbol={data.excursion.price.currencySymbol}
                        minimumAge={data.excursion.minimumAge}
                        buttonText={buttonText}
                        currentAvailability={data.currentAvailability}
                        _onClick={togglePassengerModal}
                    />
                )}
                {shouldShowSoldOut && (
                    <>
                        <SoldOutBox
                            isBookingAllowed={bookingAllowed}
                            waitlistActivated={waitlistActive}
                            currentWaitlistAvailability={data.currentWaitlistAvailability}
                        />
                        {canWaitlistBook && (
                            <PriceInfoBox
                                classes="mt-4 w-full text-primary-dark border border-primary-light p-4"
                                adultPrice={data.excursion.price.adult}
                                childPrice={data.excursion.price.child}
                                currencySymbol={data.excursion.price.currencySymbol}
                                minimumAge={data.excursion.minimumAge}
                                buttonText={buttonText}
                                currentAvailability={data.currentWaitlistAvailability}
                                _onClick={togglePassengerModal}
                                isWaitlist={true}
                            />
                        )}
                    </>
                )}
            </>
        );
    };

    return (
        <div>
            <PlannerContainer />
            <div className="container mt-4 mb-8 mx-auto">
                <button onClick={() => navigate(-1)}
                    className="text-primary cursor-pointer print-hidden flex items-center hover:underline">
                    <ThemableSvgIcon name="chevron-left" className="w-4 h-4 inline" /> 
                    <span><Trans id="back">Back</Trans></span>
                </button>
                <div className="print-text-center">
                    <h1 className="uppercase">{getTranslation(data.excursion.name)}</h1>
                </div>
                <div className="print-text-center my-4 print-my-0">
                    <ContentHeaderImage
                        altText={data.excursion.name.defaultValue + " - header image"}
                        resourceId={data.excursion.id}
                        category="cruiseExcursion"
                        width="1048"
                    >
                        <GalleryToggleButton
                            toggleGallery={images && images.length > 1 && toggleImagesModal}
                            toggleExp360Gallery={!!exp360BundleId && toggleExp360Modal}
                        />
                    </ContentHeaderImage>
                </div>
                <div className="print-hidden">
                    <ModalGallery
                        show={showImagesModal}
                        onClose={toggleImagesModal}
                    >
                        <ImageGallery
                            items={images}
                            lazyLoad={true}
                            showIndex={true}
                            defaultImage={imagePath('2_1_no_image.png')}
                            showPlayButton={false}
                        />
                    </ModalGallery>
                    <ModalGallery
                        show={showExp360Modal}
                        onClose={toggleExp360Modal}
                    >
                        <Exp360VideoPlayerSimple bundleId={exp360BundleId} />
                    </ModalGallery>
                </div>
                <div className="sm:flex sm:flex-row-reverse my-8 print-flex print-flex-row-reverse">
                    {/*right container - pricebox and key facts*/}
                    <div className="sm:w-1/2 md:w-1/3 xl:w-1/4 print-w-1-4">
                        {renderExcursionDetailPrice()}
                        <div className="mt-6">
                            <div className={`uppercase p-4 bg-primary-light text-primary-dark print-py-0`}>
                                <Trans id="key_facts">Key facts</Trans>
                            </div>
                            <div className="bg-primary-lightest p-4 print-py-0">
                                {excursionAttributes.length > 0 &&
                                    <div className="pb-2 border-b print-border-0">
                                        {useExcursionTags() ?
                                            <ExcursionTags attributes={excursionAttributes} /> :
                                            <ExcursionAttributes attributes={excursionAttributes} />
                                        }
                                    </div>
                                }
                                <div className="py-2 border-b print-border-0">
                                    <ExcursionDuration duration={data.duration ? data.duration : data.excursion.duration} />
                                </div>
                                <div className="pt-2">
                                    <ExcursionAttributes attributes={excursionFacts} />
                                </div>
                                <div className="text-primary mt-2 border-t print-border-0 py-2">
                                    <BookingCode bookingCode={data.excursion.bookingCode} />
                                    <DepartureTime fromTime={data.fromTime} endTime={data.endTime} />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*left container - about the excursion & excursion itinerary*/}
                    <div className="mt-8 sm:mt-0 sm:w-1/2 md:w-2/3 xl:w-3/4 sm:pr-4 print-w-3-4">
                        <div className="text-primary-dark mb-4">
                            <h2><Trans id="about_the_excursion">About the {getExcursionText()}</Trans></h2>
                            <ExcursionDescription description={data.description} />
                        </div>
                        <ContentDividerWithIcon
                            iconAltText="info icon"
                            iconSrc={themableImagePath('infoicon.svg')}
                            title={t({id: "please_note"})}
                            hidden={!data.notes}
                        />
                        <div className="text-grey-darker my-4"> 
                            <ImportantInformationEntry
                                content={data.notes}
                            />
                        </div>
                        {data.excursion.waiverPdfUrl &&
                            <div className="my-4">
                                <a href={`${getHubBaseUrl(false)}${data.excursion.waiverPdfUrl.url}`}
                                    className="underline text-primary"
                                    target="_blank"
                                    rel="noopener noreferrer">
                                    <Trans id="link_to_waiver">Link to Waiver</Trans>
                                </a>
                            </div>
                        }
                    </div>
                </div>
            </div>
            <RecommendationSlider
                title={t({
                        id: "popular_excursions_from",
                        message: `Popular Destination ${excursionsText} from ${portName}`
                    })}
                path={`prebooking/cruises/itinerary/${itineraryId}/recommendedExcursions`}
            />
            <Modal
                show={showPassengerModal}
                onClose={togglePassengerModal}
                title={getTranslation(data.excursion.name)?.toUpperCase()}
            >
                <BookedPassengersList
                    itineraryId={itineraryId}
                    excursionId={excursionId}
                />
                <AddToPlannerForm
                    itineraryId={itineraryId}
                    excursionId={excursionId}
                    fromTime={data.fromTime}
                    toTime={data.endTime}
                    adultPrice={data.excursion.price.adult}
                    childPrice={data.excursion.price.child}
                    currencySymbol={data.excursion.price.currencySymbol}
                    onCancel={togglePassengerModal}
                    onSuccess={showSuccessModalHandler}
                    waiverPdfUrl={data.excursion.waiverPdfUrl}
                    minimumAge={data.excursion.minimumAge}
                />
            </Modal>
            <SuccessModal
                show={showSuccessModal}
                onClose={closeSuccessModal}
                title={t({id: "excursion_reservation", message: `${excursionText} reservation`})}
                message={successMessage}
            />
        </div>
    );
};

export default ExcursionDetail;

function ExcursionDescription(props) {
    return (
        <div className="text-grey-darker my-2" dangerouslySetInnerHTML={{__html: removeHtmlStyles(props.description)}} />
    )
}
